@if (!loadingCapture) {
  <div class="capture-detail">
    @if (loadErrors.length > 0) {
      <div class="alert alert-danger d-flex align-items-center mt-3" role="alert">
        <div>
          @for (loadError of loadErrors; track loadError) {
            <div>{{ loadError }}</div>
          }
        </div>
        <button type="button" class="btn btn-sm btn-outline-danger ms-auto" (click)="onRetryClick()">Try again</button>
      </div>
      @if (!report) {
        <a routerLink="/capture-admin/captures/{{ captureId }}">&larr; Back</a>
      }
    }
    @if (!loadingReport && report) {
      <div class="capture-audit mt-3">
        <div class="d-flex align-items-center mb-3">
          <a routerLink="/capture-admin/captures/{{ captureId }}">&larr; Back</a>
          <span class="ms-auto small text-muted">Generated {{ report.generatedAt | date: 'MM/dd/yyyy hh:mm:ss a' }}</span>
          <button type="button" class="btn btn-sm btn-outline-secondary ms-2" (click)="onRefreshClick()">Refresh</button>
        </div>

        <div class="row g-3">
          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Capture Overview</h4>
                <ng-container *ngTemplateOutlet="labeledValues; context: { $implicit: overview }" />
                @if (statusTimestamps.length > 0) {
                  <h5 class="my-2">Timestamps</h5>
                  <ng-container *ngTemplateOutlet="labeledValues; context: { $implicit: statusTimestamps }" />
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Identifiers</h4>
                <ng-container *ngTemplateOutlet="labeledValues; context: { $implicit: identifiers }" />
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Client Configuration Options</h4>
                <ng-container *ngTemplateOutlet="namedValues; context: { $implicit: report.clientConfiguration.flags }" />
                <h5 class="my-2">Values</h5>
                <ng-container *ngTemplateOutlet="namedValues; context: { $implicit: report.clientConfiguration.values }" />
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Key Transitioner Predicates</h4>
                <ng-container *ngTemplateOutlet="namedValues; context: { $implicit: report.predicates }" />
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Provider</h4>
                @if (!report.provider.id) {
                  <p class="text-muted fst-italic">No provider</p>
                }
                <ng-container *ngTemplateOutlet="labeledValues; context: { $implicit: provider }" />
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Verifying Patient Attachment</h4>
                @if (report.verifyingPatientAttachment) {
                  <ng-container *ngTemplateOutlet="labeledValues; context: { $implicit: verifyingPatientAttachment }" />
                } @else {
                  <p class="text-muted fst-italic mb-0">No verifying patient attachment</p>
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Client Prescription Matches</h4>
                @if (report.clientPrescriptionMatches.matches.length > 0) {
                  <ng-container
                    *ngTemplateOutlet="labeledValues; context: { $implicit: [{ label: 'All Internal Referral', value: report.clientPrescriptionMatches.allInternalReferral }] }" />
                  <table class="table table-bordered table-sm mb-0 mt-2">
                    <thead class="table-dark"><tr><th>ID</th><th>MATCH TYPE</th></tr></thead>
                    <tbody>
                      @for (match of report.clientPrescriptionMatches.matches; track match.id) {
                        <tr><td>{{ match.id }}</td><td class="mono">{{ match.matchType }}</td></tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0">None</p>
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Referral Match Verifications</h4>
                <ng-container
                  *ngTemplateOutlet="labeledValues; context: { $implicit: [
                    { label: 'Has Valid Referral Match Verification', value: report.referralMatchVerifications.hasValidReferralMatchVerification },
                    { label: 'Has Valid Referral Match Verification Or Substitute', value: report.referralMatchVerifications.hasValidReferralMatchVerificationOrSubstitute },
                    { label: 'Count', value: report.referralMatchVerifications.totalCount }
                  ] }" />
                @if (report.referralMatchVerifications.items.length > 0) {
                  <table class="table table-bordered table-sm mb-0 mt-2">
                    <thead class="table-dark"><tr><th>ID</th><th>SUBTYPE</th></tr></thead>
                    <tbody>
                      @for (verification of report.referralMatchVerifications.items; track verification.id) {
                        <tr><td>{{ verification.id }}</td><td class="mono">{{ verification.subtype }}</td></tr>
                      }
                    </tbody>
                  </table>
                  @if (report.referralMatchVerifications.totalCount > report.referralMatchVerifications.items.length) {
                    <p class="small text-muted mb-0 mt-1">
                      Showing {{ report.referralMatchVerifications.items.length }} of {{ report.referralMatchVerifications.totalCount }}
                    </p>
                  }
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Encounters Eligible for Verification</h4>
                <ng-container
                  *ngTemplateOutlet="labeledValues; context: { $implicit: [
                    { label: 'Earliest Eligible Encounter Date', value: report.encounters.earliestEligibleEncounterDate, isDate: true }
                  ] }" />
                @if (report.encounters.items.length > 0) {
                  <table class="table table-bordered table-sm mb-0 mt-2">
                    <thead class="table-dark">
                      <tr><th>ID</th><th>DATE</th><th>TYPE</th><th>EHR IDENTIFIER</th><th>LOCATION</th></tr>
                    </thead>
                    <tbody>
                      @for (encounter of report.encounters.items; track encounter.id) {
                        <tr>
                          <td>{{ encounter.id }}</td>
                          <td class="text-nowrap">{{ encounter.encounterDate | date: 'MM/dd/yyyy' }}</td>
                          <td>{{ encounter.encounterType }}</td>
                          <td>{{ encounter.ehrIdentifier }}</td>
                          <td>{{ encounter.patientInteractionLocation }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                  @if (report.encounters.totalCount > report.encounters.items.length) {
                    <p class="small text-muted mb-0 mt-1">
                      Showing {{ report.encounters.items.length }} of {{ report.encounters.totalCount }}
                    </p>
                  }
                } @else {
                  <p class="text-muted fst-italic mb-0">None, so the patient is not under the care of the CE</p>
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Peer Captures for Status Reason</h4>
                @if (report.peerCaptures.reasonScope) {
                  <ng-container
                    *ngTemplateOutlet="labeledValues; context: { $implicit: [{ label: 'Peer Scope', value: report.peerCaptures.reasonScope }] }" />
                  @if (report.peerCaptures.statuses.length > 0) {
                    <table class="table table-bordered table-sm mb-0 mt-2">
                      <thead class="table-dark"><tr><th>STATUS</th><th class="text-end">COUNT</th></tr></thead>
                      <tbody>
                        @for (peerStatus of report.peerCaptures.statuses; track peerStatus.status) {
                          <tr>
                            <td>{{ peerStatus.status | captureStatus }}</td>
                            <td class="text-end">{{ peerStatus.count }}</td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  } @else {
                    <p class="text-muted fst-italic mb-0 mt-2">No other captures in this scope</p>
                  }
                } @else {
                  <p class="text-muted fst-italic mb-0">The current status reason does not have peer captures</p>
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Expiration</h4>
                <ng-container
                  *ngTemplateOutlet="labeledValues; context: { $implicit: [
                    { label: 'Expiration Type', value: report.expiration.expirationType },
                    { label: 'Expired', value: report.expiration.expired }
                  ] }" />
                @if (report.expiration.records.length > 0) {
                  <table class="table table-bordered table-sm mb-0 mt-2">
                    <thead class="table-dark"><tr><th>TIME</th><th>PREVIOUS STATUS</th><th>PREVIOUS REASON</th></tr></thead>
                    <tbody>
                      @for (record of report.expiration.records; track $index) {
                        <tr>
                          <td class="text-nowrap">{{ record.createdAt | date: 'MM/dd/yyyy hh:mm a' }}</td>
                          <td>{{ record.previousStatus | captureStatus }}</td>
                          <td>{{ record.previousStatusReason | captureStatusReason }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0 mt-2">No expiration records</p>
                }
              </div>
            </div>
          </div>

          <div class="col-xl-6">
            <div class="card h-100">
              <div class="card-body">
                <h4>Capture Errors</h4>
                @if (report.errors.length > 0) {
                  <table class="table table-bordered table-sm mb-0">
                    <thead class="table-dark"><tr><th>TIME</th><th>MESSAGE</th></tr></thead>
                    <tbody>
                      @for (error of report.errors; track $index) {
                        <tr>
                          <td class="text-nowrap">{{ error.createdAt | date: 'MM/dd/yyyy hh:mm a' }}</td>
                          <td class="phi">{{ error.message }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0">None</p>
                }
              </div>
            </div>
          </div>

          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <div class="d-flex align-items-center mb-2">
                  <h4 class="mb-0">Transitioners</h4>
                  <span class="small ms-auto d-flex align-items-center gap-3">
                    <span class="d-flex align-items-center gap-1">
                      <span class="legend-swatch bg-success-subtle"></span>
                      Would transition if auto advance ran now
                    </span>
                    <span class="d-flex align-items-center gap-1">
                      <span class="legend-swatch bg-warning-subtle"></span>
                      Targets the current status
                    </span>
                    <span class="d-flex align-items-center gap-1">
                      <span class="legend-swatch unknown-swatch"></span>
                      Unknown, depends on the transition above
                    </span>
                  </span>
                </div>
                <ng-container
                  *ngTemplateOutlet="labeledValues; context: { $implicit: [{ label: 'Transitioner List', value: report.transitioners.transitionerList }] }" />

                <h5 class="my-2">
                  Transitioners that could produce current status ({{ report.capture.status | captureStatus }})
                </h5>
                @if (report.transitioners.targetingCurrentStatus.length > 0) {
                  <ul class="mb-0">
                    @for (name of report.transitioners.targetingCurrentStatus; track name) {
                      <li class="mono">{{ name }}</li>
                    }
                  </ul>
                } @else {
                  <p class="text-muted fst-italic mb-0">None match the current status</p>
                }

                @if (report.transitioners.eligibility) {
                  <h5 class="my-2">
                    Eligibility if auto advance ran now
                  </h5>
                  <ul class="small text-muted mb-2">
                    <li><strong>WOULD TRANSITION</strong>: every check passes and this is the first transitioner that
                      would change the capture. Peer transitioners show the peer they would use</li>
                    <li><strong>no</strong>: not in a status this transitioner transitions from, the patient does not
                      match, there is no eligible patient interaction, or the transitioner's own rule says no</li>
                    <li><strong>no, no eligible peer</strong>: the checks pass, but no peer capture is eligible to
                      transition this capture with</li>
                    <li><strong>unknown</strong>: auto advance runs every transitioner in order, and a transitioner
                      above would transition first, so this depends on the changed capture</li>
                    <li><strong>ERROR</strong>: evaluating this transitioner raised an error</li>
                  </ul>
                  <table class="table table-bordered table-sm table-hover mb-0">
                    <thead class="table-dark">
                      <tr><th>#</th><th>TRANSITIONER</th><th>TARGET STATUS</th><th>RESULT</th></tr>
                    </thead>
                    <tbody>
                      @for (row of report.transitioners.eligibility; track row.position) {
                        <tr [class]="eligibilityRowClass(row)">
                          <td>{{ row.position }}</td>
                          <td class="mono">{{ row.name }}</td>
                          <td class="mono">{{ row.targetStatus }}</td>
                          <td>
                            @switch (row.result) {
                              @case ('error') {
                                <span class="mono text-danger">ERROR: {{ row.error }}</span>
                              }
                              @case ('would_transition') {
                                <span class="badge text-bg-success">WOULD TRANSITION</span>
                                @if (row.peerCaptureId) {
                                  with peer
                                  <a routerLink="/capture-admin/captures/{{ row.peerCaptureId }}">
                                    {{ row.peerCaptureId }}
                                  </a>
                                }
                              }
                              @case ('no_eligible_peer') {
                                <span class="text-muted">no, no eligible peer</span>
                              }
                              @case ('unknown') {
                                <span>unknown</span>
                              }
                              @default {
                                <span class="text-muted">no</span>
                              }
                            }
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0 mt-2">Terminal capture, forward eligibility is not applicable</p>
                }
              </div>
            </div>
          </div>

          <div class="col-12">
            <div class="card">
              <div class="card-body">
                <h4>Timeline (audits and history items, chronological)</h4>
                @if (report.timeline.length > 0) {
                  <table class="table table-bordered table-sm table-hover mb-0 timeline">
                    <thead class="table-dark"><tr><th>TIME</th><th>SOURCE</th><th>DETAIL</th><th>USER</th></tr></thead>
                    <tbody>
                      @for (row of report.timeline; track $index) {
                        <tr [class]="row.source + '-row'">
                          <td class="text-nowrap">{{ row.at | date: 'MM/dd/yyyy hh:mm:ss a' }}</td>
                          <td>
                            <span class="badge" [class]="row.source === 'audit' ? 'text-bg-warning' : 'text-bg-info'">
                              {{ row.source.toUpperCase() }}
                            </span>
                          </td>
                          <td class="mono">
                            @if (row.changes.length > 0) {
                              @for (change of row.changes; track change.field) {
                                <div>{{ change.field }}: {{ change.from ?? '(none)' }} &rarr; {{ change.to ?? '(none)' }}</div>
                              }
                            } @else {
                              {{ row.action }}
                            }
                          </td>
                          <td class="text-nowrap text-muted">{{ row.user ?? 'system' }}</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                } @else {
                  <p class="text-muted fst-italic mb-0">No records</p>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    } @else if (loadingReport) {
      <mat-spinner />
    }
  </div>
} @else {
  <mat-spinner />
}

<!-- label / value rows for objects converted with toLabeledValues -->
<ng-template #labeledValues let-rows>
  <table class="table table-bordered table-sm kv mb-0">
    <tbody>
      @for (row of rows; track row.label) {
        <tr>
          <th class="label table-light fw-semibold">{{ row.label }}</th>
          <td>
            @if (row.link) {
              <a [routerLink]="row.link">{{ row.value }}</a>
            } @else {
              <ng-container *ngTemplateOutlet="value; context: { $implicit: row.value, isDate: row.isDate }" />
            }
          </td>
        </tr>
      }
    </tbody>
  </table>
</ng-template>

<!-- name / value rows where the name is a code identifier (configuration option or predicate) -->
<ng-template #namedValues let-rows>
  <table class="table table-bordered table-sm kv mb-0">
    <tbody>
      @for (row of rows; track row.name) {
        <tr>
          <th class="label table-light fw-normal mono">{{ row.name }}</th>
          <td>
            <ng-container *ngTemplateOutlet="value; context: { $implicit: row.value, isDate: false }" />
          </td>
        </tr>
      }
    </tbody>
  </table>
</ng-template>

<ng-template #value let-value let-isDate="isDate">
  @if (isBoolean(value)) {
    <span class="badge" [class]="value ? 'text-bg-success' : 'text-bg-light text-muted'">{{ value ? 'YES' : 'no' }}</span>
  } @else if (isBlank(value)) {
    <span class="text-muted">--</span>
  } @else if (isObject(value)) {
    @for (entry of value | keyvalue; track entry.key) {
      <div>{{ entry.key }}: {{ entry.value | json }}</div>
    }
  } @else if (isDate) {
    {{ value | date: (value.length > 10 ? 'MM/dd/yyyy hh:mm:ss a' : 'MM/dd/yyyy') }}
  } @else {
    {{ value }}
  }
</ng-template>
